import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Ship, Plus, Search, FileDown, FileSpreadsheet, RefreshCw, Radar, PenLine, Anchor, MessageSquare, Paperclip, Trash2, Pencil, Download, X, Route, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, SIZES, TRANSIT_LABELS, TRANSIT_COLORS, fmtDate, downloadBlob } from "../lib/api";
import { fileToCompressedDataUrl } from "../lib/image";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const ESTADOS = Object.keys(TRANSIT_LABELS);
const EMPTY_FORM = {
  container_number: "", size: "", naviera: "", puerto_origen: "", pais_origen: "",
  fecha_zarpe: "", escalas: [], eta_panama: "", estado: "en_origen",
  modo: "manual", shipsgo_id: null, observacion: "",
};

function DaysBadge({ dias }) {
  if (dias == null) return <span className="text-slate-400 text-sm">-</span>;
  const tone = dias < 0 ? "text-emerald-600" : dias <= 5 ? "text-red-600" : dias <= 12 ? "text-amber-600" : "text-cyan-700";
  return (
    <div className="text-center">
      <p className={`font-outfit text-3xl font-extrabold ${tone}`} data-testid="days-remaining">{dias < 0 ? 0 : dias}</p>
      <p className="text-[10px] uppercase tracking-wider text-slate-400">{dias < 0 ? "Llegó" : "días restantes"}</p>
    </div>
  );
}

function ProgressSteps({ estado }) {
  const idx = ESTADOS.indexOf(estado);
  return (
    <div className="flex items-center gap-1" data-testid="transit-progress">
      {ESTADOS.map((s, i) => (
        <div key={s} className={`h-1.5 flex-1 rounded-full ${i <= idx ? "bg-cyan-500" : "bg-slate-200"}`} title={TRANSIT_LABELS[s]} />
      ))}
    </div>
  );
}

function TransitForm({ open, onClose, editing, carriers }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(EMPTY_FORM);
  const [mode, setMode] = useState("manual");
  const [looking, setLooking] = useState(false);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    if (editing) {
      setForm({
        container_number: editing.container_number, size: editing.size || "", naviera: editing.naviera || "",
        puerto_origen: editing.puerto_origen || "", pais_origen: editing.pais_origen || "",
        fecha_zarpe: (editing.fecha_zarpe || "").slice(0, 10), escalas: editing.escalas || [],
        eta_panama: (editing.eta_panama || "").slice(0, 10), estado: editing.estado || "en_origen",
        modo: editing.modo || "manual", shipsgo_id: editing.shipsgo_id || null, observacion: "",
      });
      setMode(editing.modo === "auto" ? "auto" : "manual");
    } else {
      setForm(EMPTY_FORM);
      setMode("manual");
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const lookup = async () => {
    if (!form.container_number.trim()) { toast.error("Ingrese el número de contenedor"); return; }
    setLooking(true);
    try {
      const { data } = await api.post("/transits/shipsgo/lookup", { container_number: form.container_number });
      setForm((f) => ({
        ...f,
        naviera: data.naviera || f.naviera,
        puerto_origen: data.puerto_origen || f.puerto_origen,
        pais_origen: data.pais_origen || f.pais_origen,
        fecha_zarpe: (data.fecha_zarpe || "").slice(0, 10) || f.fecha_zarpe,
        escalas: data.escalas?.length ? data.escalas : f.escalas,
        eta_panama: (data.eta_panama || "").slice(0, 10) || f.eta_panama,
        estado: data.estado || f.estado,
        shipsgo_id: data.shipsgo_id || null,
      }));
      toast.success("Datos obtenidos de ShipsGo. Revise y confirme el registro.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLooking(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.container_number || !form.size || !form.naviera) {
      toast.error("Complete al menos contenedor, tamaño y naviera");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      escalas: form.escalas.filter(Boolean),
      fecha_zarpe: form.fecha_zarpe || null,
      eta_panama: form.eta_panama || null,
      modo: mode,
    };
    try {
      if (editing) await api.put(`/transits/${editing.id}`, payload);
      else await api.post("/transits", payload);
      toast.success(editing ? "Tránsito actualizado" : "Tránsito registrado");
      qc.invalidateQueries({ queryKey: ["transits"] });
      qc.invalidateQueries({ queryKey: ["carriers"] });
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const setEscala = (i, v) => setForm((f) => ({ ...f, escalas: f.escalas.map((e, j) => (j === i ? v : e)) }));

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="transit-form-dialog" onOpenAutoFocus={reset}>
        <DialogHeader>
          <DialogTitle className="font-outfit flex items-center gap-2">
            <Ship className="w-5 h-5 text-cyan-600" /> {editing ? "Editar Tránsito" : "Registrar Contenedor en Tránsito"}
          </DialogTitle>
        </DialogHeader>

        {!editing && (
          <div className="grid grid-cols-2 gap-2" data-testid="transit-mode-toggle">
            <button type="button" onClick={() => setMode("auto")} data-testid="mode-auto-button"
                    className={`flex items-center justify-center gap-2 rounded-lg border-2 px-3 py-2.5 text-sm font-semibold transition-all ${
                      mode === "auto" ? "border-cyan-500 bg-cyan-50 text-cyan-800" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>
              <Radar className="w-4 h-4" /> Automático (ShipsGo)
            </button>
            <button type="button" onClick={() => setMode("manual")} data-testid="mode-manual-button"
                    className={`flex items-center justify-center gap-2 rounded-lg border-2 px-3 py-2.5 text-sm font-semibold transition-all ${
                      mode === "manual" ? "border-navy bg-slate-100 text-navy" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>
              <PenLine className="w-4 h-4" /> Manual
            </button>
          </div>
        )}

        <form onSubmit={save} className="space-y-4">
          <div className="flex gap-2 items-end">
            <div className="flex-1 space-y-1.5">
              <Label htmlFor="t-container">Número de contenedor (ISO 6346)</Label>
              <Input id="t-container" required disabled={!!editing} value={form.container_number} maxLength={11}
                     onChange={(e) => set("container_number", e.target.value.toUpperCase())}
                     placeholder="MSCU1234567" className="font-mono font-bold tracking-widest uppercase" data-testid="transit-container-input" />
            </div>
            {mode === "auto" && !editing && (
              <Button type="button" onClick={lookup} disabled={looking} data-testid="shipsgo-lookup-button"
                      className="bg-telemetry text-navy hover:bg-cyan-300 font-bold h-10">
                {looking ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Radar className="w-4 h-4 mr-1.5" />}
                {looking ? "Consultando..." : "Consultar ShipsGo"}
              </Button>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Tamaño</Label>
              <Select value={form.size} onValueChange={(v) => set("size", v)}>
                <SelectTrigger className="h-10" data-testid="transit-size-select"><SelectValue placeholder="Seleccione tamaño" /></SelectTrigger>
                <SelectContent>
                  {SIZES.map((s) => <SelectItem key={s} value={s} data-testid={`transit-size-${s.replace(/\s/g, "-")}`}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="t-naviera">Naviera</Label>
              <Input id="t-naviera" list="carriers-list" value={form.naviera} onChange={(e) => set("naviera", e.target.value)}
                     placeholder="Maersk, MSC, Hapag-Lloyd..." data-testid="transit-naviera-input" />
              <datalist id="carriers-list">
                {carriers.map((c) => <option key={c.id} value={c.nombre} />)}
              </datalist>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="t-pol" className="flex items-center gap-1"><Anchor className="w-3.5 h-3.5 text-cyan-600" /> Puerto de origen</Label>
              <Input id="t-pol" value={form.puerto_origen} onChange={(e) => set("puerto_origen", e.target.value)}
                     placeholder="Ej. Shanghái" data-testid="transit-pol-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="t-pais">País de origen</Label>
              <Input id="t-pais" value={form.pais_origen} onChange={(e) => set("pais_origen", e.target.value)}
                     placeholder="Ej. China" data-testid="transit-pais-input" />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="t-zarpe" className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5 text-cyan-600" /> Fecha de zarpe</Label>
              <Input id="t-zarpe" type="date" value={form.fecha_zarpe} onChange={(e) => set("fecha_zarpe", e.target.value)} data-testid="transit-zarpe-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="t-eta">Fecha estimada de llegada a Panamá</Label>
              <Input id="t-eta" type="date" value={form.eta_panama} onChange={(e) => set("eta_panama", e.target.value)} data-testid="transit-eta-input" />
              {!form.eta_panama && form.fecha_zarpe && (
                <p className="text-[11px] text-cyan-700" data-testid="eta-auto-note">Si se deja vacía, el sistema la calculará según la ruta y la naviera.</p>
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="flex items-center gap-1"><Route className="w-3.5 h-3.5 text-cyan-600" /> Ruta estimada — puertos de escala</Label>
            {form.escalas.map((esc, i) => (
              <div key={i} className="flex gap-2">
                <Input value={esc} onChange={(e) => setEscala(i, e.target.value)} placeholder={`Escala ${i + 1} (Ej. Busán)`}
                       data-testid={`transit-escala-${i}`} />
                <button type="button" onClick={() => setForm((f) => ({ ...f, escalas: f.escalas.filter((_, j) => j !== i) }))}
                        className="p-2 text-slate-400 hover:text-red-600" data-testid={`transit-escala-del-${i}`}><X className="w-4 h-4" /></button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => setForm((f) => ({ ...f, escalas: [...f.escalas, ""] }))}
                    className="border-dashed" data-testid="transit-add-escala">
              <Plus className="w-3.5 h-3.5 mr-1" /> Agregar escala
            </Button>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Estado en tránsito</Label>
              <Select value={form.estado} onValueChange={(v) => set("estado", v)}>
                <SelectTrigger className="h-10" data-testid="transit-estado-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ESTADOS.map((s) => <SelectItem key={s} value={s} data-testid={`transit-estado-${s}`}>{TRANSIT_LABELS[s]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="t-obs">Observación inicial (opcional)</Label>
              <Input id="t-obs" value={form.observacion} onChange={(e) => set("observacion", e.target.value)}
                     placeholder="Nota sobre este tránsito" data-testid="transit-obs-input" />
            </div>
          </div>

          <Button type="submit" disabled={saving} className="w-full h-11 bg-navy hover:bg-navy-800 text-white font-bold" data-testid="transit-save-button">
            {saving ? "Guardando..." : editing ? "Guardar Cambios" : "Registrar Tránsito"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TransitDetail({ transitId, open, onClose }) {
  const qc = useQueryClient();
  const [obs, setObs] = useState("");
  const { data: t } = useQuery({
    queryKey: ["transit-detail", transitId],
    queryFn: async () => (await api.get(`/transits/${transitId}`)).data,
    enabled: open && !!transitId,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["transit-detail", transitId] });
    qc.invalidateQueries({ queryKey: ["transits"] });
  };

  const addObs = async (e) => {
    e.preventDefault();
    if (!obs.trim()) return;
    try {
      await api.post(`/transits/${transitId}/observaciones`, { texto: obs });
      setObs("");
      refresh();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const onDocFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const data = file.type.startsWith("image/")
        ? await fileToCompressedDataUrl(file, 1200, 800 * 1024)
        : await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
      await api.post(`/transits/${transitId}/documentos`, { nombre: file.name, data });
      toast.success("Documento adjuntado");
      refresh();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    e.target.value = "";
  };

  const downloadDoc = async (doc) => {
    const res = await api.get(`/transits/${transitId}/documentos/${doc.id}`, { responseType: "blob" });
    downloadBlob(res.data, doc.nombre);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="transit-detail-dialog">
        {t && (
          <>
            <DialogHeader>
              <DialogTitle className="font-outfit flex items-center gap-2 flex-wrap">
                <span className="font-mono tracking-wider">{t.container_number}</span>
                <Badge variant="outline" className={TRANSIT_COLORS[t.estado]} data-testid="transit-detail-status">{TRANSIT_LABELS[t.estado]}</Badge>
                <Badge variant="secondary" className="text-[10px]">{t.modo === "auto" ? "ShipsGo" : "Manual"}</Badge>
              </DialogTitle>
            </DialogHeader>
            <ProgressSteps estado={t.estado} />
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm mt-2">
              {[
                ["Naviera", t.naviera || "-"],
                ["Tamaño", t.size || "-"],
                ["Puerto de Origen", t.puerto_origen || "-"],
                ["País de Origen", t.pais_origen || "-"],
                ["Fecha de Zarpe", fmtDate(t.fecha_zarpe)],
                ["ETA Panamá", `${fmtDate(t.eta_panama)}${t.eta_calculada ? " (calculada)" : ""}`],
                ["Días Restantes", t.dias_restantes != null ? String(Math.max(0, t.dias_restantes)) : "-"],
                ["Escalas", t.escalas?.length ? t.escalas.join(" → ") : "Directo"],
                ["Registrado", fmtDate(t.created_at)],
              ].map(([k, v]) => (
                <div key={k} className="bg-slate-50 rounded-lg px-3 py-2" data-testid={`transit-detail-${k.toLowerCase().replace(/[\s/()]/g, "-")}`}>
                  <p className="text-[10px] uppercase tracking-wider text-slate-400">{k}</p>
                  <p className="font-semibold text-navy">{v}</p>
                </div>
              ))}
            </div>

            <div className="mt-4 space-y-2">
              <h4 className="font-outfit font-bold text-sm flex items-center gap-2"><MessageSquare className="w-4 h-4 text-cyan-600" /> Observaciones</h4>
              <div className="space-y-1.5 max-h-40 overflow-y-auto" data-testid="transit-observations-list">
                {(t.observaciones || []).length === 0 && <p className="text-xs text-slate-400">Sin observaciones</p>}
                {(t.observaciones || []).map((o) => (
                  <div key={o.id} className="bg-slate-50 rounded-lg px-3 py-2 text-sm">
                    <p className="text-slate-700">{o.texto}</p>
                    <p className="text-[10px] text-slate-400">{o.autor} · {fmtDate(o.fecha)}</p>
                  </div>
                ))}
              </div>
              <form onSubmit={addObs} className="flex gap-2">
                <Input value={obs} onChange={(e) => setObs(e.target.value)} placeholder="Agregar observación..." data-testid="transit-obs-add-input" />
                <Button type="submit" size="sm" className="bg-navy hover:bg-navy-800 text-white shrink-0" data-testid="transit-obs-add-button">Agregar</Button>
              </form>
            </div>

            <div className="mt-4 space-y-2">
              <h4 className="font-outfit font-bold text-sm flex items-center gap-2"><Paperclip className="w-4 h-4 text-cyan-600" /> Documentos Adjuntos</h4>
              <div className="space-y-1.5" data-testid="transit-documents-list">
                {(t.documentos || []).length === 0 && <p className="text-xs text-slate-400">Sin documentos</p>}
                {(t.documentos || []).map((d) => (
                  <div key={d.id} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
                    <span className="text-sm text-slate-700 truncate">{d.nombre}</span>
                    <button type="button" onClick={() => downloadDoc(d)} className="p-1.5 text-cyan-700 hover:bg-cyan-50 rounded" data-testid={`transit-doc-download-${d.nombre}`}>
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <label className="flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 hover:border-cyan-500 rounded-lg px-3 py-2.5 text-sm text-slate-500 cursor-pointer transition-colors"
                     data-testid="transit-doc-upload">
                <Paperclip className="w-4 h-4" /> Adjuntar documento (máx. 2 MB)
                <input type="file" className="hidden" onChange={onDocFile} accept="image/*,.pdf,.doc,.docx,.xls,.xlsx" data-testid="transit-doc-input" />
              </label>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Transito() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [naviera, setNaviera] = useState("todas");
  const [estado, setEstado] = useState("todos");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailId, setDetailId] = useState(null);

  const { data: transits = [] } = useQuery({
    queryKey: ["transits", q, naviera, estado],
    queryFn: async () => (await api.get("/transits", {
      params: { q, naviera: naviera === "todas" ? "" : naviera, estado: estado === "todos" ? "" : estado },
    })).data,
    refetchInterval: 30000,
  });
  const { data: carriers = [] } = useQuery({ queryKey: ["carriers"], queryFn: async () => (await api.get("/carriers")).data });

  const activos = transits.filter((t) => t.estado !== "llegado_panama");

  const exportFile = async (fmt) => {
    const res = await api.get(`/transits/report/${fmt}`, {
      params: { q, naviera: naviera === "todas" ? "" : naviera }, responseType: "blob",
    });
    downloadBlob(res.data, `historial_transitos.${fmt === "excel" ? "xlsx" : "pdf"}`);
  };

  const refreshFromShipsgo = async (t) => {
    try {
      await api.post(`/transits/${t.id}/refresh`);
      toast.success(`${t.container_number} actualizado desde ShipsGo`);
      qc.invalidateQueries({ queryKey: ["transits"] });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (t) => {
    if (!window.confirm(`¿Eliminar el tránsito de ${t.container_number}?`)) return;
    await api.delete(`/transits/${t.id}`);
    toast.success("Tránsito eliminado");
    qc.invalidateQueries({ queryKey: ["transits"] });
  };

  return (
    <div className="space-y-6" data-testid="transito-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-outfit text-2xl md:text-3xl font-extrabold tracking-tight flex items-center gap-2">
            <Ship className="w-6 h-6 text-cyan-600" /> Trazabilidad en Tránsito
          </h1>
          <p className="text-sm text-slate-500 flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-rampgreen pulse-dot inline-block" /> Días restantes actualizados automáticamente
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => exportFile("pdf")} className="border-navy text-navy" data-testid="transit-export-pdf">
            <FileDown className="w-4 h-4 mr-2" /> PDF
          </Button>
          <Button variant="outline" onClick={() => exportFile("excel")} className="border-emerald-700 text-emerald-700" data-testid="transit-export-excel">
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
          </Button>
          <Button onClick={() => { setEditing(null); setFormOpen(true); }} className="bg-navy hover:bg-navy-800 text-white" data-testid="new-transit-button">
            <Plus className="w-4 h-4 mr-2" /> Nuevo Tránsito
          </Button>
        </div>
      </div>

      <Card className="border-slate-200" data-testid="active-transits-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-outfit text-lg">Contenedores en Tránsito hacia Panamá ({activos.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {activos.length === 0 && <p className="text-sm text-slate-400 py-6 text-center" data-testid="active-transits-empty">No hay contenedores en tránsito</p>}
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {activos.map((t) => (
              <button key={t.id} onClick={() => setDetailId(t.id)} data-testid={`transit-card-${t.container_number}`}
                      className="text-left rounded-xl border border-slate-200 hover:border-cyan-400 hover:shadow-sm transition-all p-4 space-y-3 bg-white">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono font-bold tracking-wider text-navy">{t.container_number}</span>
                  <Badge variant="outline" className={`${TRANSIT_COLORS[t.estado]} text-[10px]`}>{TRANSIT_LABELS[t.estado]}</Badge>
                </div>
                <p className="text-xs text-slate-500">
                  {t.naviera || "-"} · {t.puerto_origen || "?"} → <span className="font-semibold text-navy">Panamá</span>
                  {t.escalas?.length > 0 && <span className="block text-[10px] text-slate-400">Vía: {t.escalas.join(" → ")}</span>}
                </p>
                <div className="flex items-end justify-between gap-2">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-slate-400">ETA Panamá{t.eta_calculada ? " (est.)" : ""}</p>
                    <p className="font-mono text-sm font-bold text-navy">{fmtDate(t.eta_panama)}</p>
                  </div>
                  <DaysBadge dias={t.dias_restantes} />
                </div>
                <ProgressSteps estado={t.estado} />
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por número de contenedor..."
                 className="pl-9 h-11" data-testid="transit-search-input" />
        </div>
        <Select value={naviera} onValueChange={setNaviera}>
          <SelectTrigger className="sm:w-48 h-11" data-testid="transit-naviera-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todas" data-testid="naviera-filter-todas">Todas las navieras</SelectItem>
            {carriers.map((c) => <SelectItem key={c.id} value={c.nombre} data-testid={`naviera-filter-${c.nombre}`}>{c.nombre}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={estado} onValueChange={setEstado}>
          <SelectTrigger className="sm:w-52 h-11" data-testid="transit-estado-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos" data-testid="estado-filter-todos">Todos los estados</SelectItem>
            {ESTADOS.map((s) => <SelectItem key={s} value={s} data-testid={`estado-filter-${s}`}>{TRANSIT_LABELS[s]}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="border-slate-200" data-testid="transit-history-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-outfit text-lg">Historial de Tránsitos</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-navy hover:bg-navy">
                <TableHead className="text-white">Contenedor</TableHead>
                <TableHead className="text-white">Naviera</TableHead>
                <TableHead className="text-white">Origen</TableHead>
                <TableHead className="text-white">Zarpe</TableHead>
                <TableHead className="text-white">ETA Panamá</TableHead>
                <TableHead className="text-white">Días</TableHead>
                <TableHead className="text-white">Estado</TableHead>
                <TableHead className="text-white text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transits.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-10" data-testid="transit-history-empty">Sin tránsitos registrados</TableCell></TableRow>
              )}
              {transits.map((t) => (
                <TableRow key={t.id} className="cursor-pointer" onClick={() => setDetailId(t.id)} data-testid={`transit-row-${t.container_number}`}>
                  <TableCell className="font-mono font-bold tracking-wider">{t.container_number}</TableCell>
                  <TableCell className="text-sm">{t.naviera || "-"}</TableCell>
                  <TableCell className="text-xs">{t.puerto_origen || "-"}{t.pais_origen ? `, ${t.pais_origen}` : ""}</TableCell>
                  <TableCell className="text-xs">{fmtDate(t.fecha_zarpe)}</TableCell>
                  <TableCell className="text-xs">{fmtDate(t.eta_panama)}{t.eta_calculada ? " *" : ""}</TableCell>
                  <TableCell className="font-mono font-bold">{t.dias_restantes != null ? Math.max(0, t.dias_restantes) : "-"}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`${TRANSIT_COLORS[t.estado]} text-[10px] whitespace-nowrap`}>{TRANSIT_LABELS[t.estado]}</Badge>
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    {t.modo === "auto" && (
                      <button onClick={() => refreshFromShipsgo(t)} className="p-2 text-slate-400 hover:text-cyan-600" title="Actualizar desde ShipsGo"
                              data-testid={`transit-refresh-${t.container_number}`}><RefreshCw className="w-4 h-4" /></button>
                    )}
                    <button onClick={() => { setEditing(t); setFormOpen(true); }} className="p-2 text-slate-400 hover:text-cyan-600"
                            data-testid={`transit-edit-${t.container_number}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(t)} className="p-2 text-slate-400 hover:text-red-600"
                            data-testid={`transit-delete-${t.container_number}`}><Trash2 className="w-4 h-4" /></button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {formOpen && <TransitForm open={formOpen} onClose={() => setFormOpen(false)} editing={editing} carriers={carriers} />}
      <TransitDetail transitId={detailId} open={!!detailId} onClose={() => setDetailId(null)} />
    </div>
  );
}
